import { csrfToken } from 'application/utils/csrf-request'

type State = { sort: string; filtered: boolean; searched: boolean }
type EventData = State & {
  event_id: string
  kind: 'impression' | 'click'
  listing_id: string
  quest_id: number
  position: number
  viewport: string
}

const SELECTOR = 'article.quest-card[data-quest-id]'

// One exposure per quest/listing. Filtering or sorting creates a new listing;
// "show more" keeps the listing and absolute positions (helper cards excluded).
export class CatalogAnalytics {
  private observer?: IntersectionObserver
  private timers = new Map<HTMLElement, ReturnType<typeof setTimeout>>()
  private visible = new Set<HTMLElement>()
  private sent = new Set<string>()
  private queue: EventData[] = []
  private flushTimer?: ReturnType<typeof setTimeout>
  private listing = ''
  private context = ''
  private state: State = { sort: 'default', filtered: false, searched: false }

  constructor(private container: HTMLElement) {
    this.context =
      container.closest<HTMLElement>('[data-quests-catalog]')?.dataset
        .analyticsContext || ''
    if (
      !this.context ||
      typeof IntersectionObserver === 'undefined' ||
      !globalThis.crypto?.randomUUID
    )
      return

    this.observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const card = entry.target as HTMLElement
          if (entry.isIntersecting && entry.intersectionRatio >= 0.5) {
            this.visible.add(card)
            this.startTimer(card)
          } else {
            this.visible.delete(card)
            this.cancelTimer(card)
          }
        })
      },
      { threshold: [0, 0.5] }
    )
    container.addEventListener('click', (event) => this.onClick(event))
    container.addEventListener('auxclick', (event) => {
      if (event.button === 1) this.onClick(event)
    })
    document.addEventListener('visibilitychange', () => {
      this.timers.forEach((_, card) => this.cancelTimer(card))
      if (document.visibilityState === 'visible') {
        this.visible.forEach((card) => this.startTimer(card))
      } else {
        this.flush()
      }
    })
    window.addEventListener('pagehide', () => this.flush())
    this.refresh(false, this.state)
  }

  refresh(append: boolean, state: State): void {
    if (!this.observer) return
    if (!append) {
      this.flush()
      this.observer.disconnect()
      this.timers.forEach((_, card) => this.cancelTimer(card))
      this.visible.clear()
      this.sent.clear()
      this.listing = crypto.randomUUID()
      this.state = { ...state }
    }
    this.container.querySelectorAll<HTMLElement>(SELECTOR).forEach((card) => {
      this.observer!.observe(card)
    })
  }

  private startTimer(card: HTMLElement): void {
    if (
      document.visibilityState !== 'visible' ||
      this.timers.has(card) ||
      this.sent.has(`impression:${card.dataset.questId}`)
    )
      return
    this.timers.set(
      card,
      setTimeout(() => {
        this.timers.delete(card)
        if (document.visibilityState === 'visible' && this.visible.has(card)) {
          this.record('impression', card)
        }
      }, 1000)
    )
  }

  private cancelTimer(card: HTMLElement): void {
    clearTimeout(this.timers.get(card))
    this.timers.delete(card)
  }

  private onClick(event: Event): void {
    const target = event.target as Element | null
    if (!target?.closest('a, button')) return
    const card = target.closest<HTMLElement>(SELECTOR)
    if (!card || !this.container.contains(card)) return
    this.record('click', card)
    this.flush()
  }

  private record(kind: EventData['kind'], card: HTMLElement): void {
    const id = Number(card.dataset.questId)
    const position =
      Array.from(this.container.querySelectorAll(SELECTOR)).indexOf(card) + 1
    const key = `${kind}:${id}`
    if (!position || !Number.isInteger(id) || id <= 0 || this.sent.has(key))
      return
    if (kind === 'impression') this.sent.add(key)
    this.queue.push({
      // eslint-disable-next-line camelcase
      event_id: crypto.randomUUID(),
      ...this.state,
      kind,
      // eslint-disable-next-line camelcase
      listing_id: this.listing,
      // eslint-disable-next-line camelcase
      quest_id: id,
      position,
      viewport: window.innerWidth < 768 ? 'mobile' : 'desktop'
    })
    if (this.queue.length >= 20) this.flush()
    else if (!this.flushTimer)
      this.flushTimer = setTimeout(() => this.flush(), 1000)
  }

  private flush(): void {
    clearTimeout(this.flushTimer)
    delete this.flushTimer
    while (this.queue.length) this.deliver(this.queue.splice(0, 20), 0)
  }

  private deliver(events: EventData[], attempt: number): void {
    try {
      const token = csrfToken()
      fetch('/api/catalog_events', {
        method: 'POST',
        credentials: 'same-origin',
        keepalive: true,
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { 'X-CSRF-Token': token } : {})
        },
        body: JSON.stringify({ context: this.context, events })
      })
        .then((response) => {
          if (response.status >= 500 || response.status === 429)
            throw new Error('Retry analytics')
        })
        .catch(() => {
          if (attempt < 2)
            setTimeout(
              () => this.deliver(events, attempt + 1),
              2000 * (attempt + 1)
            )
        })
    } catch {
      // Analytics must never interrupt catalog navigation or booking.
    }
  }
}
